import { Button, Skeleton, Transfer, TransferProps } from "antd";
import { useState, useMemo, useEffect } from "react";
import ExportHelper from "../../utils/ExportHelper";
import _ from "lodash";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faWarning } from "@fortawesome/free-solid-svg-icons";
import { enmPdfExportType } from "../../models/enums/PdfExportType";
import { useTranslation } from "react-i18next";
import { enmExportModalType } from "../../models/enums/ExportModalType";
import { useAppDispatch } from "../../store/Hooks";
import { setPdfExportObject } from "../../store/SiteSlice";
import { ExcelColumnMappings, enmExcelExportType } from "../../utils/ExcelColumnMappings";

interface TransferDataType {
    key: string;
    title: string;
    description: string;
}

export type ExportModalProps = {
    type?: enmExportModalType;
    pdfType?: enmPdfExportType;
    name: string;
    data?: object[];
    fnModalClose?: Function;
    excelExportType?: enmExcelExportType;
}

export const ExportModal = (props: ExportModalProps) => {
    const dispatch = useAppDispatch();
    const { t } = useTranslation();

    const defaultColumns = useMemo(() => {
        const firstItem = _.first(props.data) as object;
        if (!firstItem) return [];

        // Excel export type varsa ve ExcelColumnMappings içinde tanımlıysa
        if (props.excelExportType) {
            const mapping = ExcelColumnMappings.find(x => x.type === props.excelExportType);
            if (mapping) {
                return mapping.columns.map<TransferDataType>((col) => ({
                    key: col.name,
                    title: col.label,
                    description: '',
                }));
            }
        }

        // Normal durum için tüm kolonları göster
        return Object.keys(firstItem).map<TransferDataType>((x, i) => ({
            key: x,
            title: x,
            description: '',
        }));
    }, [props.data, props.excelExportType]);

    const [targetKeys, setTargetKeys] = useState<TransferProps['targetKeys']>([]);
    const [selectedKeys, setSelectedKeys] = useState<TransferProps['targetKeys']>([]);

    // defaultColumns değiştiğinde targetKeys'i güncelle
    useEffect(() => {
        setTargetKeys(defaultColumns.map(x => x.key));
    }, [defaultColumns]);

    const onSelectChange: TransferProps['onSelectChange'] = (
        sourceSelectedKeys,
        targetSelectedKeys,
    ) => {
        setSelectedKeys([...sourceSelectedKeys, ...targetSelectedKeys]);
    };

    const onChange: TransferProps['onChange'] = (nextTargetKeys, direction, moveKeys) => {
        setTargetKeys(nextTargetKeys);
    };

    const fnExport = async () => {
        if (!targetKeys || targetKeys.length == 0) {
            return;
        }

        const selectedColumns = targetKeys?.map<string>((x) => x.toString());
        if (!selectedColumns) {
            return;
        }

        if (props.type && props.type == enmExportModalType.PDFTable) {
            if (props.excelExportType) {
                const mapping = ExcelColumnMappings.find(x => x.type === props.excelExportType);
                if (mapping) {
                    // Parent kolonları filtrele
                    const parentColumns = selectedColumns.filter(x => !x.includes("_"));

                    // PDF için başlıkları mapping'den al
                    const headLabels = parentColumns.map(h => {
                        const column = mapping.columns.find(c => c.name === h);
                        return column?.label || h;
                    });

                    // PDF için verileri hazırla ve renderBefore uygula
                    const rows = props.data?.map((obj: any) =>
                        parentColumns.map((x: string) => {
                            const column = mapping.columns.find(c => c.name === x);
                            if (column) {
                                const value = obj[x];
                                return column.renderBefore && props.excelExportType !== enmExcelExportType.EntryList
                                    ? column.renderBefore(value)
                                    : value;
                            }
                            return '';
                        })
                    ) ?? [];

                    const pdfProps = ExportHelper.exportToPDFTable(`${props.name}`, props.pdfType ?? enmPdfExportType.PDF, headLabels, rows);
                    dispatch(setPdfExportObject(pdfProps));
                } else {
                    const pdfProps = ExportHelper.exportToPDFTable(`${props.name}`, props.pdfType ?? enmPdfExportType.PDF, selectedColumns, props.data?.map((obj: any) => selectedColumns.map((x: string) => obj[x])) ?? []);
                    dispatch(setPdfExportObject(pdfProps));
                }
            } else {
                const pdfProps = ExportHelper.exportToPDFTable(`${props.name}`, props.pdfType ?? enmPdfExportType.PDF, selectedColumns, props.data?.map((obj: any) => selectedColumns.map((x: string) => obj[x])) ?? []);
                dispatch(setPdfExportObject(pdfProps));
            }
        } else {
            // Excel export için özel işlem
            if (props.excelExportType) {
                const mapping = ExcelColumnMappings.find(x => x.type === props.excelExportType);
                if (mapping) {
                    const rows: any[] = [];
                    const CHILD_INDENT = 2;

                    // Parent kolonları filtrele (child kolonları hariç tut)
                    const parentColumns = selectedColumns.filter(x => !x.includes("_"));

                    props.data?.forEach((obj: any) => {
                        // Parent verilerini içeren satır
                        const mainRow = new Array(parentColumns.length).fill('');
                        parentColumns.forEach((x: string, index: number) => {
                            const column = mapping.columns.find(c => c.name === x);
                            if (column) {
                                const value = obj[x];
                                mainRow[index] = column.renderBefore
                                    ? column.renderBefore(value)
                                    : value;
                            }
                        });
                        rows.push(mainRow);

                        // Child rows işlemleri - Birden fazla child grup için
                        type ChildColumnType = {
                            selector: string;
                            columns: Array<{
                                name: string;
                                label: string;
                                renderBefore?: (value: any) => any;
                            }>;
                            orderColumn?: string;
                        };

                        const childColumnsArray: ChildColumnType[] = Array.isArray(mapping.columnChilds)
                            ? mapping.columnChilds
                            : (mapping.columnChilds ? [mapping.columnChilds] : []);

                        childColumnsArray.forEach((childColumns: ChildColumnType) => {
                            if (childColumns && selectedColumns.some(x => x === childColumns.selector)) {
                                const rawChildData = obj[childColumns.selector.replaceAll("_", "")];

                                // Child verisi array değilse direkt veriyi işle
                                if (!Array.isArray(rawChildData)) {
                                    if (rawChildData) {
                                        // Child başlık satırı
                                        const childHeaderRow = new Array(parentColumns.length).fill('');
                                        childColumns.columns.forEach((col, index) => {
                                            const column = mapping.columns.find(c => c.name === col.name);
                                            childHeaderRow[CHILD_INDENT + index] = {
                                                value: column?.label || col.label,
                                                style: {
                                                    fill: {
                                                        type: 'pattern',
                                                        pattern: 'solid',
                                                        fgColor: { argb: 'FF8DB4E2' } // Tekil child'lar için mavi ton
                                                    }
                                                }
                                            };
                                        });
                                        rows.push(childHeaderRow);

                                        // Child veri satırı
                                        const childRow = new Array(parentColumns.length).fill('');
                                        childColumns.columns.forEach((col, colIndex) => {
                                            const value = rawChildData;
                                            childRow[CHILD_INDENT + colIndex] = {
                                                value: col.renderBefore ? col.renderBefore(value) : value
                                            };
                                        });
                                        rows.push(childRow);
                                    }
                                } else if (rawChildData.length > 0) {
                                    // Array ise mevcut mantıkla devam et
                                    // Child başlık satırı
                                    const childHeaderRow = new Array(parentColumns.length).fill('');
                                    childColumns.columns.forEach((col, index) => {
                                        const column = mapping.columns.find(c => c.name === col.name);
                                        childHeaderRow[CHILD_INDENT + index] = {
                                            value: column?.label || col.label,
                                            style: {
                                                fill: {
                                                    type: 'pattern',
                                                    pattern: 'solid',
                                                    fgColor: { argb: 'FF9BBB6A' } // Array child'lar için yeşil ton
                                                }
                                            }
                                        };
                                    });
                                    rows.push(childHeaderRow);

                                    // Child verileri sırala
                                    let orderedChildData = [...rawChildData];
                                    if (childColumns.orderColumn) {
                                        orderedChildData = _.orderBy(rawChildData, [childColumns.orderColumn], ['asc']);
                                    }

                                    // Child veri satırları
                                    orderedChildData.forEach((child: any) => {
                                        const childRow = new Array(parentColumns.length).fill('');
                                        childColumns.columns.forEach((col, colIndex) => {
                                            const value = child[col.name];
                                            childRow[CHILD_INDENT + colIndex] = {
                                                value: col.renderBefore ? col.renderBefore(value) : value
                                            };
                                        });
                                        rows.push(childRow);
                                    });
                                }
                            }
                        });
                    });

                    // Excel'e gönderilecek başlıklar - mapping'den label'ları al
                    const headLabels = parentColumns.map(h => {
                        const column = mapping.columns.find(c => c.name === h);
                        return {
                            label: column?.label || h,
                            style: { fill: { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFD3D3D3' } } } // Gri arka plan
                        };
                    });

                    // Child başlıklar için stiller
                    const childArrayHeaderStyle = {
                        fill: {
                            type: 'pattern',
                            pattern: 'solid',
                            fgColor: { argb: 'FF9BBB6A' } // Array child'lar için yeşil ton
                        }
                    };

                    const childSingleHeaderStyle = {
                        fill: {
                            type: 'pattern',
                            pattern: 'solid',
                            fgColor: { argb: 'FF8DB4E2' } // Tekil child'lar için mavi ton
                        }
                    };

                    interface ColumnType {
                        name: string;
                        label: string;
                        renderBefore?: (value: any) => any;
                    }

                    interface ChildColumnType {
                        selector: string;
                        columns: ColumnType[];
                        orderColumn?: string;
                    }

                    const processedRows = rows.map((row, rowIndex) => {
                        // Child başlık satırı tespiti - Birden fazla child grup için kontrol
                        if (Array.isArray(row)) {
                            const firstNonEmptyIndex = row.findIndex(cell => cell !== '');
                            const isChildHeader = firstNonEmptyIndex >= CHILD_INDENT;

                            if (isChildHeader) {
                                // Herhangi bir child grubun başlığı mı kontrol et
                                const rowContent = row.slice(CHILD_INDENT);
                                const childColumnsArray: ChildColumnType[] = Array.isArray(mapping.columnChilds)
                                    ? mapping.columnChilds
                                    : (mapping.columnChilds ? [mapping.columnChilds] : []);

                                const isHeaderRow = childColumnsArray.some((childColumns: ChildColumnType) =>
                                    rowContent.some(cell =>
                                        childColumns.columns.some((col: ColumnType) => col.label === cell)
                                    )
                                );

                                if (isHeaderRow) {
                                    // İlgili child grubun kolon sayısını ve tipini bul
                                    const relevantChildColumns = childColumnsArray.find((childColumns: ChildColumnType) =>
                                        rowContent.some(cell =>
                                            childColumns.columns.some((col: ColumnType) => col.label === cell)
                                        )
                                    );

                                    // Child verinin array olup olmadığını kontrol et
                                    const parentRow = Math.floor((rowIndex - 1) / 2); // Her parent için bir başlık ve veri satırı olduğundan
                                    const parentData = props.data?.[parentRow] as { [key: string]: any };
                                    const childData = relevantChildColumns && parentData
                                        ? parentData[relevantChildColumns.selector.replaceAll("_", "")]
                                        : null;
                                    const isArrayChild = Array.isArray(childData);

                                    const childColumnCount = relevantChildColumns?.columns.length || 0;
                                    return row.map((cell, cellIndex) => {
                                        if (cellIndex >= CHILD_INDENT && cellIndex < CHILD_INDENT + childColumnCount) {
                                            return {
                                                value: cell,
                                                style: isArrayChild ? childArrayHeaderStyle : childSingleHeaderStyle
                                            };
                                        }
                                        return cell;
                                    });
                                }
                            }
                        }
                        return row;
                    });

                    await ExportHelper.exportToExcel(`${props.name}`, headLabels, processedRows);
                } else {
                    // Normal export
                    const rows = props.data?.map((obj: any) => selectedColumns.map((x: string) => obj[x]));
                    await ExportHelper.exportToExcel(`${props.name}`, selectedColumns, rows ?? []);
                }
            } else {
                // Normal export
                const rows = props.data?.map((obj: any) => selectedColumns.map((x: string) => obj[x]));
                await ExportHelper.exportToExcel(`${props.name}`, selectedColumns, rows ?? []);
            }
        }
    };

    const fnCloseModal = () => {
        props.fnModalClose && props.fnModalClose();
    };

    return (
        <div className="row">

            {
                !props.data || props.data?.length == 0
                &&
                <>
                    <div className="col-md-12">
                        <Skeleton active />
                        <Skeleton active />
                    </div>
                </>
            }
            {
                !props.data
                &&
                <>
                    <div className="col-md-12 mt-2">
                        <div className="alert alert-warning alert-icon">
                            <FontAwesomeIcon icon={faWarning} />
                            <span className="ps-4">Data not found.</span>
                        </div>
                    </div>
                </>
            }
            {
                props.data && props.data.length > 0
                &&
                <>
                    <div className="col-md-12">
                        <Transfer
                            dataSource={defaultColumns}
                            titles={['Source', 'Target']}
                            targetKeys={targetKeys}
                            selectedKeys={selectedKeys}
                            onChange={onChange}
                            onSelectChange={onSelectChange}
                            render={(item) => item.title}
                        />
                    </div>
                    <div className="col-md-12 mt-4 text-end">
                        <Button className="btn btn-secondary btn-dim me-2 " onClick={fnExport}>{props.type && props.type == enmExportModalType.PDFTable ? t("common.exportToPDF") : t("common.exportToExcel")}</Button>
                        <Button className="btn btn-danger btn-dim me-2 " onClick={fnCloseModal}>{t("common.close")}</Button>
                    </div>
                </>
            }
        </div>
    );
};
